import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Switch, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import styles from './InlineEditableField.module.scss';

function normalizeDraft(value, type) {
    if (type === 'multiselect') {
        return Array.isArray(value) ? value : [];
    }

    if (type === 'checkbox') {
        return Boolean(value);
    }

    return value ?? '';
}

function optionLabel(options, value) {
    const item = (Array.isArray(options) ? options : []).find((option) => String(option.value) === String(value));

    return item?.label || '';
}

export default function InlineEditableField({
    value,
    displayValue,
    type = 'text',
    options = [],
    onSave,
    placeholder = '—',
    disabled = false,
    className = '',
    viewClassName = '',
    rows = 4,
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(() => normalizeDraft(value, type));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!editing) {
            setDraft(normalizeDraft(value, type));
        }
    }, [editing, type, value]);

    const viewText = useMemo(() => {
        if (displayValue !== undefined && displayValue !== null && String(displayValue).trim() !== '') {
            return String(displayValue);
        }

        if (type === 'checkbox') {
            return value ? 'Да' : 'Нет';
        }

        if (type === 'select') {
            return optionLabel(options, value) || placeholder;
        }

        if (type === 'multiselect') {
            const labels = (Array.isArray(value) ? value : [])
                .map((item) => optionLabel(options, item))
                .filter(Boolean);

            return labels.length > 0 ? labels.join(', ') : placeholder;
        }

        const text = value === null || value === undefined ? '' : String(value);

        return text.trim() !== '' ? text : placeholder;
    }, [displayValue, options, placeholder, type, value]);

    const cancel = () => {
        setDraft(normalizeDraft(value, type));
        setEditing(false);
    };

    const save = async () => {
        if (typeof onSave !== 'function') {
            setEditing(false);
            return;
        }

        try {
            setSaving(true);
            await onSave(draft);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const onKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            cancel();
            return;
        }

        if (type === 'textarea') {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                void save();
            }
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            void save();
        }
    };

    const renderEditor = () => {
        if (type === 'textarea') {
            return (
                <Input.TextArea
                    autoFocus
                    value={draft}
                    rows={rows}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={onKeyDown}
                />
            );
        }

        if (type === 'select') {
            return (
                <Select
                    autoFocus
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    value={draft === '' ? undefined : draft}
                    options={options}
                    onChange={(nextValue) => setDraft(nextValue ?? '')}
                    onKeyDown={onKeyDown}
                    className={styles.select}
                />
            );
        }

        if (type === 'multiselect') {
            return (
                <Select
                    autoFocus
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    value={Array.isArray(draft) ? draft : []}
                    options={options}
                    onChange={(nextValue) => setDraft(nextValue)}
                    onKeyDown={onKeyDown}
                    className={styles.select}
                />
            );
        }

        if (type === 'checkbox') {
            return <Switch checked={Boolean(draft)} onChange={setDraft} />;
        }

        return (
            <input
                autoFocus
                type={type === 'date' ? 'date' : 'text'}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={onKeyDown}
                className={styles.input}
            />
        );
    };

    if (editing) {
        return (
            <div className={`${styles.editor} ${className}`}>
                {renderEditor()}
                <div className={styles.actions}>
                    <Tooltip title="Сохранить">
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={saving}
                            onClick={() => void save()}
                        />
                    </Tooltip>
                    <Tooltip title="Отмена">
                        <Button size="small" icon={<CloseOutlined />} onClick={cancel} disabled={saving} />
                    </Tooltip>
                </div>
            </div>
        );
    }

    return (
        <button
            type="button"
            className={`${styles.view} ${viewClassName} ${className}`}
            onClick={() => !disabled && setEditing(true)}
            disabled={disabled}
        >
            {viewText}
        </button>
    );
}
